import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Trash2,
  Mail,
  CheckCircle2,
  Circle,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";

const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const INTEREST_FILTERS = [
  "All",
  "Trade Off",
  "Construction software / bespoke development",
  "Website from £49/month",
  "Not specified",
];

export const AdminEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/enquiries");
      setEnquiries(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHandled = async (enq) => {
    setBusyId(enq.id);
    try {
      const { data } = await adminApi.patch(`/admin/enquiries/${enq.id}`, {
        handled: !enq.handled,
      });
      setEnquiries((list) => list.map((x) => (x.id === enq.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (enq) => {
    if (!window.confirm(`Delete enquiry from ${enq.name}? This cannot be undone.`)) return;
    setBusyId(enq.id);
    try {
      await adminApi.delete(`/admin/enquiries/${enq.id}`);
      setEnquiries((list) => list.filter((x) => x.id !== enq.id));
      toast.success("Enquiry deleted.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = enquiries.filter((e) => {
    if (filter === "All") return true;
    if (filter === "Not specified") return !e.interest;
    return e.interest === filter;
  });
  const unhandled = enquiries.filter((e) => !e.handled).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-800 text-3xl text-brand-navy">Enquiries</h1>
          <p className="mt-1 text-sm text-slate-500">
            {enquiries.length} total ·{" "}
            <span className={unhandled ? "text-brand-blue font-semibold" : ""}>
              {unhandled} new
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Interest</label>
          <select
            data-testid="enquiries-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
          >
            {INTEREST_FILTERS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-brand-blue" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-500 rounded-2xl border border-slate-200 bg-white">
            <Inbox className="h-8 w-8 mx-auto text-slate-300" />
            <p className="mt-3">No enquiries {filter !== "All" ? "for this filter" : "yet"}.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((enq) => (
              <div
                key={enq.id}
                data-testid={`enquiry-card-${enq.id}`}
                className={`rounded-2xl border bg-white p-5 transition-colors ${
                  enq.handled ? "border-slate-200 opacity-70" : "border-brand-blue/30"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-heading font-700 text-brand-navy">{enq.name}</p>
                      {enq.company && (
                        <span className="text-sm text-slate-400">· {enq.company}</span>
                      )}
                      {!enq.handled && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue">
                          New
                        </span>
                      )}
                    </div>
                    <a
                      href={`mailto:${enq.email}`}
                      className="mt-1 inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {enq.email}
                    </a>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {formatDateTime(enq.created_at)}
                  </span>
                </div>

                {enq.interest && (
                  <span className="mt-3 inline-block rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                    {enq.interest}
                  </span>
                )}

                <p className="mt-3 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                  {enq.message}
                </p>

                <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                  <button
                    data-testid={`enquiry-toggle-${enq.id}`}
                    onClick={() => toggleHandled(enq)}
                    disabled={busyId === enq.id}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                      enq.handled
                        ? "text-slate-500 hover:bg-slate-100"
                        : "text-green-700 hover:bg-green-50"
                    }`}
                  >
                    {busyId === enq.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : enq.handled ? (
                      <Circle className="h-4 w-4" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    {enq.handled ? "Mark as new" : "Mark handled"}
                  </button>
                  <a
                    href={`mailto:${enq.email}?subject=Re: your enquiry to Blue Shuck Consulting`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-brand-navy hover:bg-slate-100"
                  >
                    <Mail className="h-4 w-4" />
                    Reply
                  </a>
                  <button
                    data-testid={`enquiry-delete-${enq.id}`}
                    onClick={() => remove(enq)}
                    disabled={busyId === enq.id}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
